import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { isIOSMobileBrowser } from "@/utils/device";
import i18n from "@/i18n/i18n";
import { DEFAULT_APP_LANGUAGE } from "@/types/language";
import HomeBeetleLayout from "@/components/home/home-beetle-layout";

const APP_STORE_URL = process.env.REACT_APP_APP_STORE_URL || "";
const PLAY_STORE_URL = process.env.REACT_APP_PLAY_STORE_URL || "";

// Picks the browser language this page supports; zh-TW/zh-HK/zh-Hant map to zh-HK.
const detectLanguage = () => {
  for (const lang of navigator.languages ?? [navigator.language]) {
    const l = lang.toLowerCase();
    if (l.startsWith("zh")) {
      return /hk|tw|mo|hant/.test(l) ? "zh-HK" : "zh-CN";
    }
    if (l.startsWith("en")) return "en";
  }
  return DEFAULT_APP_LANGUAGE;
};

const Invite: React.FC = () => {
  const t = i18n.getFixedT(detectLanguage());
  const [searchParams] = useSearchParams();
  const startedRef = useRef(false);
  const code = searchParams.get("code");
  const [returned, setReturned] = useState(false);

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    redirectToStore();
  }, []);

  // Safari keeps this page open behind the store app. Stop the spinner once the user comes back.
  useEffect(() => {
    let wasHidden = false;
    const onVisibility = () => {
      if (document.visibilityState === "hidden") wasHidden = true;
      else if (wasHidden) setReturned(true);
    };
    const onPageShow = (e: PageTransitionEvent) => {
      if (e.persisted) setReturned(true);
    };
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("pageshow", onPageShow);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("pageshow", onPageShow);
    };
  }, []);

  const getStoreUrl = () => {
    if (isIOSMobileBrowser()) return APP_STORE_URL;
    return code
      ? `${PLAY_STORE_URL}&referrer=${encodeURIComponent(`code=${code}`)}`
      : PLAY_STORE_URL;
  };

  const redirectToStore = () => {
    window.location.replace(getStoreUrl());
  };

  if (returned) {
    return (
      <div className="relative z-10 flex flex-col items-center gap-8 px-8 pb-2 text-center">
        <p className="text-[18px] font-bold leading-[150%] text-[#26283A]">
          {t("invite_continue")}
        </p>
        <button
          className="w-full rounded-full bg-[#0EC458] h-[60px] text-[20px] font-bold leading-none text-white shadow-[0_5px_0_#148F3D] active:translate-y-[2px] active:shadow-[0_3px_0_#148F3D]"
          onClick={() => window.location.assign(getStoreUrl())}
        >
          {t("invite_open_store")}
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-1 p-4 items-center justify-center">
      <div className="h-12 w-12 animate-spin rounded-full border-4 border-[#0EC458]/20 border-t-[#0EC458]" />
    </div>
  );
};

const InvitePage: React.FC = () => {
  return <HomeBeetleLayout children={<Invite />}></HomeBeetleLayout>;
};

export default InvitePage;
